import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { LinkButtonComponent } from '../link-button.component';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  of,
  pairwise,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { catchError, finalize } from 'rxjs/operators';
import { debugLog } from '../../utils/debug-logger';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Clipboard } from '@angular/cdk/clipboard';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { InfoTooltipComponent } from '../info-tooltip.component';
import { AddressSearchResult } from '../../../api/models/address-search-result';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { SimpleMapComponent, SimpleMapLocation } from '../simple-map.component';
import { AddressesService } from '../../../api/services/addresses.service';
import { ApiMappingsStore } from '../../../features/admin/modules/features/api-mappings/api-mapping.store';
import { UserStore } from '../../../core/services/user.store';
import { UtilityService } from '../../services/utility.service';
import { EntryPatchService } from './services/entry-patch.service';
import { ClearFieldButtonComponent } from './clear-field-button.component';
import { ClearableFieldType } from './clearable-field.type';
import { TippyTooltipDirective } from '../../directives/tippy-tooltip.directive';

let nextAddressHeadingId = 0;

@Component({
  selector: 'formly-field-address',
  template: `
    <!--
      Single-line style (Figma "*Registered company address") drops the separate heading: the
      field's own name becomes the input's label, so it lines up with every other field on the
      card instead of reading as a new section.
    -->
    @if (!singleLine()) {
      <h3 [id]="headingId" class="mb-2 flex items-center gap-1">
        {{ props.label }}
        <app-info-tooltip [tooltip]="props.info"></app-info-tooltip>
      </h3>
    }
    <div class="block float-left w-full">
      <!--
        A disabled field hides the search box in the standard style — there is nothing to search
        for and the address itself is rendered by the sub-fields underneath. In the single-line
        style that box IS the address, so it stays visible and the control is disabled instead
        (syncSingleLineDisabled), otherwise a read-only viewer would see no address at all.
      -->
      <mat-form-field
        class="w-full"
        [class.hidden]="isDisabled() && !singleLine()"
        [floatLabel]="singleLine() ? 'always' : 'auto'"
        [class.mat-form-field-invalid]="addressSearchControl.invalid && addressSearchControl.touched">
        @if (singleLine()) {
          <!-- floatLabel="always" above is what keeps the placeholder visible on an EMPTY field:
               Material hides a placeholder while its label has not floated, which would leave a new
               company staring at a blank box with no hint that typing searches. -->
          <mat-label>
            {{ props.label }}
            <app-info-tooltip [tooltip]="props.info"></app-info-tooltip>
          </mat-label>
        }
        <!--
          Two label strategies, both from Figma. The STANDARD style (node 2652:117626) has no
          <mat-label> on purpose: the field's own name is the heading above, and Material only
          gives a placeholder opacity while its label is absent or floating, so adding one would
          hide "Zoek adres" until the user clicks. The SINGLE-LINE style needs the label on the
          field itself, and pays for it with floatLabel="always" above.

          The magnifier follows the same split: Figma's single-line address has no prefix icon
          once it holds an address, so it only appears while there is nothing to show — exactly
          when the field would otherwise be an unexplained empty box.
        -->
        @if (showSearchAffordance()) {
          <mat-icon
            matPrefix
            class="icon-size-5 mr-2 text-hint"
            svgIcon="heroicons_outline:magnifying-glass"></mat-icon>
        }
        <!--
          Single-line style keeps the composed address in the control, so the field reads as a
          filled address rather than an empty search box; typing over it still searches, and
          leaving it without picking a suggestion restores the stored line (onSearchBlur). While
          the manual fields are open it goes read-only: those fields are then the single editable
          source, and two editable copies of the same address is what made the old layout confusing.
          The tooltip is what keeps that read-only state from being silent — clicking a box that
          swallows every keystroke without explanation is indistinguishable from a broken field.
          It survives the click (hideOnClick false) because clicking is exactly the gesture it
          explains; tippy's default would drop it at the moment the user tries to type.
        -->
        <input
          matInput
          [formControl]="addressSearchControl"
          [matAutocomplete]="addressAuto"
          [readonly]="searchLineLocked()"
          [tippyTooltip]="manualEntryLockTooltip()"
          [tippyTooltipHideOnClick]="false"
          placeholder="Zoek adres"
          [attr.aria-labelledby]="singleLine() ? null : headingId"
          (focus)="onSearchFocus()"
          (blur)="onSearchBlur()"
          data-cy="address-search-input" />
        @if (isSearchingAddresses()) {
          <div matSuffix class="flex items-center gap-2 mr-2">
            <mat-spinner diameter="20"></mat-spinner>
            <span class="text-xs text-primary">Zoeken...</span>
          </div>
        } @else {
          <span matSuffix class="dd-clear-suffix">
            <app-clear-field-button
              [field]="field"
              [hasValue]="addressSearchControl.value !== ''"
              [disabled]="isDisabled()"
              ariaLabel="Adres zoeken wissen"
              (clearSelection)="clearAddressSearch()" />
          </span>
        }
        <!--
          displayWith is what keeps the input readable: Material writes the selected option's VALUE
          into the native input before it emits optionSelected, and that value is the raw
          AddressSearchResult — so without this the field flashes "[object Object]".
        -->
        <mat-autocomplete
          #addressAuto="matAutocomplete"
          [displayWith]="displaySuggestion"
          (optionSelected)="onAddressSelected($event.option.value)"
          autoActiveFirstOption>
          @for (address of addressSuggestions; track $index) {
            <mat-option [value]="address">{{ formatSuggestionLabel(address) }}</mat-option>
          }
        </mat-autocomplete>
      </mat-form-field>
      <!-- The pin row repeats what the single-line input already shows, so it only renders in
           the standard style. -->
      @if (
        !singleLine() &&
        this.props.showFullAddress === true &&
        this.field.formControl.get('fullAddress')?.value !== '' &&
        this.field.formControl.get('fullAddress')?.value !== null &&
        this.field.formControl.get('fullAddress')?.value !== undefined
      ) {
        <div class="mb-4 flex items-center gap-2 rounded ">
          <mat-icon class="text-hint icon-size-6 flex-shrink-0">location_on</mat-icon>
          <span class="flex-1 text-sm text-default text-opacity-80 select-all">
            {{ this.field.formControl.get('fullAddress')?.value }}
          </span>
        </div>
      }
      <!--
        Figma "Enter address manually" (node 2652:117626, Address section). One-way on purpose:
        the design shows the link only while the manual fields are hidden. Once opened they stay
        open, which also avoids fighting the valueChanges handler that re-opens them whenever the
        address has values. The composite value persists a "manual" marker, so manual mode reopens
        even before address data exists. Address data itself continues to follow a selected
        suggestion or the manually entered fields; selecting a suggestion clears the marker.
      -->
      <!--
        Figma pairs the two links on one row: manual entry on the left, copy on the right. The row
        renders as soon as either side has something to offer, so a filled address that is no longer
        editable still exposes "Adres kopiëren".
      -->
      @if ((!showAddressFields() && !isDisabled()) || (canResumeSearch() && !isDisabled()) || canCopyAddress()) {
        <!-- Wraps rather than squeezes: at phone width the two links do not fit on one row, and
             without flex-wrap both labels break mid-phrase into two ragged lines. -->
        <div class="mb-4 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 clear">
          <div class="flex items-center gap-2">
            @if (!showAddressFields() && !isDisabled()) {
              @if (addressSearchYieldedNothing()) {
                <span class="text-sm text-hint">Geen adres gevonden.</span>
              }
              <!-- Quieter than "Adres kopiëren" on the right of the same row: this is the fallback,
                   not the expected action. See the muted tone in link-button.component.ts. -->
              <button
                type="button"
                dd-link-button
                tone="muted"
                icon="heroicons_outline:pencil-square"
                iconClass="icon-size-5"
                (click)="enableManualAddressEntry()"
                data-cy="address-manual-entry">
                Adres handmatig invoeren
              </button>
            } @else if (canResumeSearch() && !isDisabled()) {
              <!-- The way back. Without it the single-line style is a one-way door: the manual
                   fields open and the only remaining action is a clipboard copy. -->
              <button
                type="button"
                dd-link-button
                tone="muted"
                icon="heroicons_outline:magnifying-glass"
                iconClass="icon-size-5"
                (click)="resumeAddressSearch()"
                data-cy="address-resume-search">
                Adres opnieuw zoeken
              </button>
            }
          </div>
          @if (canCopyAddress()) {
            <button
              type="button"
              dd-link-button
              [icon]="addressCopied() ? 'heroicons_outline:check' : 'heroicons_outline:document-duplicate'"
              iconClass="icon-size-5"
              (click)="copyAddress()"
              data-cy="address-copy">
              {{ addressCopied() ? 'Gekopieerd' : 'Adres kopiëren' }}
            </button>
          }
        </div>
      }
      @if (showAddressFields()) {
        @for (field of field.fieldGroup; track $index) {
          @if (field.key === 'geoRadius') {
            <mat-form-field
              [class]="field.className"
              [class.mat-form-field-invalid]="field.formControl.invalid && field.formControl.touched">
              <mat-label>{{ props.geoRadiusLabel || 'Geofencing-straal' }}</mat-label>
              <input
                matInput
                type="number"
                [formControl]="field.formControl"
                [formlyAttributes]="field"
                [required]="field.props.required" />
              <span matSuffix class="dd-clear-suffix dd-addon-suffix">
                <span class="dd-addon-suffix__passive pr-5">{{ getGeoRadiusUnit() === 'km' ? 'km' : 'meter' }}</span>
                <app-clear-field-button
                  [field]="field"
                  [hasValue]="hasGeoRadiusValue(field)"
                  [disabled]="isDisabled()"
                  ariaLabel="Geofencing-straal wissen"
                  (clearSelection)="clearGeoRadius(field)" />
              </span>
              <mat-hint>{{
                getGeoRadiusUnit() === 'km'
                  ? 'Radius in kilometers rond het geselecteerde adres'
                  : 'Radius in meters rond het geselecteerde adres'
              }}</mat-hint>
              @if (field.formControl.touched && field.formControl.hasError('required')) {
                <mat-error>{{ props.geoRadiusLabel || 'Geofencing-straal' }} is verplicht.</mat-error>
              }
            </mat-form-field>
          } @else if (field.key !== 'geoRadius') {
            <formly-field [field]="field" [class]="field.props.className"></formly-field>
          }
        }
      }
      @if (hasGeolocation() && readyToloadMap() && showMap()) {
        <app-simple-map
          class="float-left mb-4 w-full"
          [location]="locationData()"
          [geoRadius]="getGeoRadius()"
          [allowMapSelection]="!isDisabled()"
          (locationSelected)="onLocationSelected($event)">
        </app-simple-map>
      } @else if (showMap()) {
        <div class="bg-light h-[382px] float-left w-full mb-4"></div>
      }
      @if (isBagVerifyVisible()) {
        <div class="w-full float-left clear">
          <div class="mb-4 flex items-center gap-2 rounded">
            @if (bagLastCheckDate(); as checkedAt) {
              <mat-icon class="text-primary icon-size-6 flex-shrink-0">verified</mat-icon>
              <div class="flex-1 min-w-0">
                <div class="text-sm text-default font-medium">Geverifieerd via BAG</div>
                <div class="text-xs text-accent">
                  Laatste controle {{ checkedAt | date: "d MMM yyyy 'om' HH:mm" : undefined : 'nl' }}
                </div>
              </div>
              <button
                mat-icon-button
                type="button"
                (click)="verifyBag()"
                [disabled]="isVerifyingBag() || isDisabled()"
                matTooltip="Opnieuw verifiëren"
                class="w-8 h-8"
                data-cy="bag-verify-button">
                @if (isVerifyingBag()) {
                  <mat-spinner diameter="16"></mat-spinner>
                } @else {
                  <mat-icon class="text-hint icon-size-4">refresh</mat-icon>
                }
              </button>
            } @else {
              <mat-icon class="text-hint icon-size-6 flex-shrink-0">verified</mat-icon>
              <div class="flex-1 min-w-0">
                <div class="text-sm text-default">Adres nog niet geverifieerd via BAG</div>
                <div class="text-xs text-accent">
                  Controleer of het adres bestaat in de Basisregistratie Adressen en Gebouwen
                </div>
              </div>
              <button
                mat-flat-button
                type="button"
                (click)="verifyBag()"
                [disabled]="isVerifyingBag() || isDisabled()"
                class="min-w-[140px] dd-action-button md-button"
                data-cy="bag-verify-button">
                @if (isVerifyingBag()) {
                  <mat-spinner diameter="16" class="inline-block align-middle"></mat-spinner>
                  Verifiëren...
                } @else {
                  <mat-icon class="icon-size-4">verified</mat-icon>
                  Verifieer via BAG
                }
              </button>
            }
          </div>
          @if (bagVerifyError()) {
            <div class="mb-4 flex items-start gap-2 text-sm text-warn">
              <mat-icon class="icon-size-5 flex-shrink-0 mt-0.5">error_outline</mat-icon>
              <span class="flex-1">{{ bagVerifyError() }}</span>
            </div>
          }
        </div>
      }
    </div>
    @if (field.props.description) {
      <mat-hint class="inline-block mb-2">{{ field.props.description }}</mat-hint>
    }
  `,
  imports: [
    ReactiveFormsModule,
    MatAutocompleteModule,
    FormlyModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    InfoTooltipComponent,
    MatProgressSpinner,
    SimpleMapComponent,
    MatButtonModule,
    MatTooltipModule,
    DatePipe,
    LinkButtonComponent,
    ClearFieldButtonComponent,
    TippyTooltipDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyFieldAddress extends ClearableFieldType implements OnInit {
  private readonly addressesService = inject(AddressesService);
  private readonly clipboard = inject(Clipboard);
  private readonly apiMappingsStore = inject(ApiMappingsStore);
  private readonly userStore = inject(UserStore);
  private readonly utilityService = inject(UtilityService);
  private readonly entryPatchService = inject(EntryPatchService);
  addressSuggestions: AddressSearchResult[] = [];
  isSearchingAddresses = signal(false);
  /** True when a non-empty search returned nothing — surfaces the manual-entry fallback. */
  addressSearchYieldedNothing = signal(false);
  addressCopied = signal(false);
  /** The one-line address behind the copy link; mirrors the fullAddress control. */
  copyableAddress = signal('');
  readonly canCopyAddress = computed(() => this.copyableAddress().trim() !== '');
  /**
   * The magnifier is a hint that typing searches. The standard style always shows it; the
   * single-line style only while the field is empty, because Figma draws a filled address line
   * without a prefix icon.
   */
  readonly showSearchAffordance = computed(() => !this.singleLine() || this.copyableAddress().trim() === '');
  /**
   * Single-line style with the manual fields open: offer the way back to the search line. The
   * disabled check stays in the template — `isDisabled()` reads the control's status, which is not
   * a signal, so a computed would not re-evaluate when it flips.
   */
  readonly canResumeSearch = computed(() => this.singleLine() && this.showAddressFields());
  /**
   * The one state in which the single line refuses input: the manual fields are open and own the
   * address, so the line only mirrors them.
   */
  readonly searchLineLocked = computed(() => this.singleLine() && this.showAddressFields());
  /**
   * Explains that refusal on hover. The generic locked-field tooltip (#20830) deliberately covers
   * disabled fields only — readonly fields stay interactive — and this line is read-only through
   * component state that no Formly extension can see, so it carries its own wording and points at
   * the way back.
   */
  readonly manualEntryLockTooltip = computed(() =>
    this.searchLineLocked() ? 'Dit adres wordt handmatig ingevuld. Kies "Adres opnieuw zoeken" om weer te zoeken.' : null
  );
  hasGeolocation = signal<boolean>(false);
  readyToloadMap = signal<boolean>(false);
  locationData = signal<SimpleMapLocation | null>(null);
  showAddressFields = signal<boolean>(false);
  /**
   * Figma's single-line address (props.addressStyle === 'single'): one input carrying the composed
   * address under the field's own label, with the individual fields collapsed behind "Adres
   * handmatig invoeren". Read once in ngOnInit — props are a plain object, not a signal.
   */
  readonly singleLine = signal(false);
  isVerifyingBag = signal(false);
  bagVerifyError = signal<string | null>(null);
  private destroy$ = injectDestroy();
  #applyingBagResult = false;
  /**
   * True between the first keystroke of a query and the moment the user leaves the field. Only the
   * single-line style needs it: it decides whether the stored address may overwrite what is
   * currently in the input. Every programmatic write uses `emitEvent: false`, so an emitted
   * valueChanges is by definition user input.
   */
  #isTypingQuery = false;
  // Monotonic id for the in-flight address search. switchMap cancels a superseded request only
  // when a NEW value emits on the stream; blur/clear/pick abandon a query WITHOUT emitting, so a
  // late response from the abandoned query could still land on a freshly retyped one. Every request
  // captures the token at start; the subscribe drops any response whose token is no longer current.
  #queryToken = 0;
  /**
   * The query that last reached the provider, or null once that query was abandoned. Clearing,
   * leaving the field and picking a suggestion all reset it, so searching the SAME term again
   * afterwards still fires a request (#20984). This replaces distinctUntilChanged() on the search
   * stream: that operator remembers the previous emission no matter what happened in between, and
   * those three routes write to the control with `emitEvent: false` — the write the operator never
   * sees. Pasting the term back makes it arrive as one emission identical to the remembered one,
   * which was silently dropped: no request, no spinner, no suggestions.
   */
  #lastExecutedQuery: string | null = null;

  showMap = signal(false);
  addressSearchControl = new FormControl<string>('', { nonNullable: true });
  readonly headingId = `address-heading-${nextAddressHeadingId++}`;

  readonly isBagVerifyVisible = computed(() => {
    if (this.props['showBagVerify'] !== true) return false;
    if (!this.props['bagApiMappingId']) return false;
    const showForRoles = this.props['bagVerifyShowUserRole'] as Record<string, boolean> | undefined;
    const hasAllowList = !!showForRoles && Object.values(showForRoles).some(v => v === true);
    if (!hasAllowList) return true;
    const currentRoleIds = (this.userStore.currentUserRoles?.() ?? []) as string[];
    return currentRoleIds.some(roleId => showForRoles![roleId] === true);
  });

  bagLastCheckDate = signal<string | undefined>(undefined);

  constructor() {
    super();
  }

  ngOnInit() {
    this.singleLine.set(this.props['addressStyle'] === 'single');

    if (this.field.fieldGroup) {
      // "Toon kaart en geolocatie" (props.showMap) gates the geolocation block.
      // When it is off, the geo sub-fields are hidden via hideGeoField(). A hidden
      // but required control silently blocks form validity (no visible error), so
      // force the geo sub-fields optional whenever geolocation is disabled —
      // regardless of the required flag the parent address field cascades down.
      const geoEnabled = this.props.showMap === true;
      const geoFieldKeys = ['latitude', 'longitude', 'geoRadius'];

      this.field.fieldGroup.forEach(field => {
        if (field.key === 'numberSuffix') {
          field.props.required = false;
        }

        if (!geoEnabled && geoFieldKeys.includes(field.key as string)) {
          field.props.required = false;
          // Re-run validators so an already-applied required error is cleared.
          field.formControl?.updateValueAndValidity({ emitEvent: false });
        }

        if (field.key === 'geoRadius') {
          const isKm = this.getGeoRadiusUnit() === 'km';
          if (
            field.formControl.value === null ||
            field.formControl.value === undefined ||
            field.formControl.value === ''
          ) {
            field.formControl.setValue(isKm ? 1 : 100);
          }
          // No upper bound — operationally useful radii vary widely (e.g. 100 km).
          delete field.props.max;
        }
      });
    }

    this.field.formControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          const fullAddress = this.createFullAddress();
          const value = this.field.formControl.value;

          this.updateGeolocationSignals(value);

          // Single-line style keeps the individual fields collapsed until the user asks for them;
          // an existing address is shown on the one line instead of unfolding the whole block.
          if (!this.showAddressFields() && !this.singleLine() && this.hasExistingAddress()) {
            this.showAddressFields.set(true);
          }

          const fullAddressControl = this.field.formControl.get('fullAddress');
          if (fullAddressControl && fullAddress !== fullAddressControl.value) {
            fullAddressControl.patchValue(fullAddress);
          }

          this.copyableAddress.set(fullAddress ?? '');
          this.syncSingleLineValue();
        })
      )
      .subscribe();

    const bagApiMappingId = this.props['bagApiMappingId'];
    if (this.props['showBagVerify'] === true && typeof bagApiMappingId === 'string' && bagApiMappingId.trim() !== '') {
      this.field.formControl.valueChanges
        .pipe(
          takeUntil(this.destroy$),
          startWith(this.field.formControl.value),
          map(value => this.completenessKey(value)),
          pairwise(),
          filter(
            ([previous, current]) => previous !== current && this.field.formControl.dirty && !this.#applyingBagResult
          )
        )
        .subscribe(() => this.invalidateBagSnapshot());
    }

    this.initializeAutoGeocode();
    this.initializeAddressSearch();
    this.setFullAddress();
    // valueChanges only fires on edits, so seed the copy link from whatever is already stored.
    this.copyableAddress.set(this.createFullAddress() ?? '');
    this.syncSingleLineValue();
    this.syncSingleLineDisabled();
    // A field can be disabled later through `expressions: { 'props.disabled': ... }` or a role
    // change, which surfaces as a status change on the composite control.
    this.field.formControl.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.syncSingleLineDisabled());
    this.showMap.set(this.props.showMap === true);
    this.hideGeoField();
    this.readyToloadMap.set(true);

    // Manual entry always unfolds the fields. The other two triggers — an address already exists,
    // or the module asked to always show every field — are what the single-line style replaces
    // with one composed line, so they only apply to the standard style.
    if (
      this.field.formControl.get('manual')?.value === true ||
      (!this.singleLine() && (this.hasExistingAddress() || this.props.showFullAddress === true))
    ) {
      this.showAddressFields.set(true);
    }

    const root = UtilityService.getFormOfRootForm(this.field);
    const bagLastCheckDateControl = root?.formControl?.get('meta-data.bag-last-check-date');
    this.bagLastCheckDate.set(bagLastCheckDateControl?.value as string | undefined);
    bagLastCheckDateControl?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => this.bagLastCheckDate.set(typeof value === 'string' && value ? value : undefined));
  }

  hideGeoField() {
    this.field.fieldGroup?.forEach(field => {
      if (
        this.showMap() === false &&
        (field.type === 'title' || field.key === 'latitude' || field.key === 'longitude' || field.key === 'geoRadius')
      ) {
        field.className = 'hidden';
      }
    });
  }

  getGeoRadiusUnit(): string {
    return this.props.geoRadiusUnit || 'meter';
  }

  getGeoRadius(): number {
    try {
      const geoRadiusControl = this.field.formControl.get('geoRadius');
      if (geoRadiusControl) {
        const value = geoRadiusControl.value || 100;
        return this.getGeoRadiusUnit() === 'km' ? value * 1000 : value;
      }
      return 100;
    } catch (e) {
      console.error('Error getting geo radius:', e);
      return 100;
    }
  }

  onLocationSelected(coordinates: { latitude: number; longitude: number }) {
    if (this.isDisabled()) {
      return;
    }

    try {
      if (this.field.formControl) {
        const update = {
          latitude: coordinates.latitude.toString(),
          longitude: coordinates.longitude.toString(),
        };

        this.field.formControl.patchValue(update);
      }
    } catch (e) {
      console.error('Error updating coordinates:', e);
    }
  }

  onAddressSelected(address: AddressSearchResult) {
    if (!address || this.isDisabled()) {
      return;
    }

    this.applySelectedAddress(address);
    // Standard style empties the search box again; single-line keeps showing the address it just
    // stored (applySelectedAddress -> valueChanges -> syncSingleLineValue already wrote it).
    if (!this.singleLine()) {
      this.addressSearchControl.setValue('', { emitEvent: false });
    }
    this.addressSuggestions = [];
  }

  /**
   * Mirrors the stored address into the visible line. Only in the single-line style, and never
   * while the user is typing a query — overwriting the input mid-search would fight the
   * autocomplete. `emitEvent: false` keeps it out of the search stream.
   */
  private syncSingleLineValue(): void {
    if (!this.singleLine() || this.#isTypingQuery) {
      return;
    }

    const fullAddress = this.createFullAddress() ?? '';
    if (this.addressSearchControl.value !== fullAddress) {
      this.addressSearchControl.setValue(fullAddress, { emitEvent: false });
    }
  }

  /**
   * Leaving the field without picking a suggestion restores the stored address, so a half-typed
   * query never masquerades as the saved value. A picked suggestion has already rewritten the
   * line by the time blur runs, so this is a no-op in the happy path.
   */
  onSearchBlur(): void {
    if (!this.singleLine()) {
      return;
    }

    this.#isTypingQuery = false;
    // Invalidate any in-flight request: leaving the field abandons the query, and a response that
    // resolves after this must not be applied (empty → "Geen adres gevonden." under a valid
    // address; non-empty → a resurrected suggestion list).
    this.#queryToken++;
    this.forgetExecutedQuery();
    // Deliberately NOT clearing addressSuggestions here. Clicking a suggestion blurs the input
    // first: emptying the list on blur destroys the very option the mouse is pressing, so the
    // click never lands and nothing is applied. Onemptying happens on the way back IN instead —
    // see onSearchFocus().
    this.addressSearchYieldedNothing.set(false);
    this.syncSingleLineValue();
  }

  /**
   * Coming back into the field must not resurrect the suggestions of a query that was abandoned:
   * the line shows the stored address again, so a list underneath it belongs to text that is no
   * longer there. Focus is the safe moment for this — unlike blur, it cannot race with the click
   * that is selecting an option, and Material opens its panel from here on.
   */
  onSearchFocus(): void {
    if (this.#lastExecutedQuery === null) {
      this.addressSuggestions = [];
      this.addressSearchYieldedNothing.set(false);
    }
  }

  /**
   * Keeps the search control in step with immutable field states: a disabled single-line field
   * keeps its composed address visible in a disabled control.
   *
   * Disabled only — read mode never reaches this component. `address` is a wrappable composite
   * leaf, so `props.readonly` makes `ReadonlyWrapper` render a label/value row INSTEAD of the
   * field and Formly never builds the type component at all (readonly/readonly.wrapper.ts,
   * #21239).
   */
  private syncSingleLineDisabled(): void {
    if (!this.singleLine()) {
      return;
    }

    const shouldDisable = this.isDisabled();
    if (shouldDisable && this.addressSearchControl.enabled) {
      this.addressSearchControl.disable({ emitEvent: false });
    } else if (!shouldDisable && this.addressSearchControl.disabled) {
      this.addressSearchControl.enable({ emitEvent: false });
    }
  }

  enableManualAddressEntry(): void {
    if (this.isDisabled()) {
      return;
    }

    this.field.formControl.patchValue({ manual: true });
    this.field.formControl.markAsDirty();
    this.field.formControl.markAsTouched();
    this.showAddressFields.set(true);
  }

  /**
   * Folds the manual fields back up and hands the single line back to the search. The counterpart
   * to enableManualAddressEntry, so the single-line style is not a one-way door. Standard style
   * keeps its old one-way behaviour: there the fields also open on their own whenever an address
   * exists, so collapsing them would just bounce straight back open.
   */
  resumeAddressSearch(): void {
    if (this.isDisabled()) {
      return;
    }

    this.field.formControl.patchValue({ manual: false });
    this.field.formControl.markAsDirty();
    this.field.formControl.markAsTouched();
    this.showAddressFields.set(false);
    this.syncSingleLineValue();
  }

  clearAddressSearch(): void {
    // emitEvent: false keeps the "user is typing" invariant true — an emitted valueChanges on this
    // control means keystrokes, nothing else. The suggestion list is cleared here instead of
    // through the search stream.
    this.addressSearchControl.setValue('', { emitEvent: false });
    this.#isTypingQuery = false;
    // Abandon any in-flight request so its late response can't repopulate the just-cleared list.
    this.#queryToken++;
    this.forgetExecutedQuery();
    this.addressSuggestions = [];
    this.addressSearchYieldedNothing.set(false);

    // In the single-line style the input IS the address, so clearing it has to clear the stored
    // parts too — otherwise the field looks empty while the entry still carries an address.
    if (this.singleLine()) {
      // Mark first: BAG invalidation observes the synchronous patch below and only invalidates
      // user edits. Coordinates belong to the cleared address and must not survive it.
      this.field.formControl.markAsDirty();
      this.field.formControl.markAsTouched();
      this.field.formControl.patchValue({
        streetName: '',
        number: '',
        numberSuffix: '',
        postalCode: '',
        city: '',
        country: '',
        fullAddress: '',
        // Clear the coordinates too — otherwise the map stays on the old location and an empty
        // address could be saved carrying stale lat/lng.
        latitude: '',
        longitude: '',
        // Wiping the address is a fresh start, so drop the manual marker with it.
        manual: false,
      });
      // Reset the map/geolocation state to match the now-empty coordinates.
      this.updateGeolocationSignals(this.field.formControl.value);
      // Hand the line back to the search. Leaving the manual fields open would leave an EMPTY
      // read-only line behind: nothing to mirror, nothing to type in, and only the "Adres opnieuw
      // zoeken" link as a way out. Safe against the auto-reopen in the valueChanges handler above,
      // which only fires in the standard style.
      this.showAddressFields.set(false);
    }
  }

  hasGeoRadiusValue(field: FormlyFieldConfig): boolean {
    const value = field.formControl?.value;
    return value !== null && value !== undefined && value !== '';
  }

  clearGeoRadius(field: FormlyFieldConfig): void {
    field.formControl?.setValue(null);
    field.formControl?.markAsDirty();
    field.formControl?.markAsTouched();
    this.field.formControl.markAsDirty();
    this.field.formControl.markAsTouched();
  }

  createFullAddress() {
    const streetName = this.field.formControl.get('streetName')?.value || '';
    const number = this.field.formControl.get('number')?.value || '';
    const numberSuffix = this.field.formControl.get('numberSuffix')?.value || '';
    const postalCode = this.field.formControl.get('postalCode')?.value || '';
    const city = this.field.formControl.get('city')?.value || '';
    const country = this.field.formControl.get('country')?.value || '';

    if (streetName !== '' || postalCode !== '' || city !== '') {
      const addressComponents = [streetName + ' ' + number + numberSuffix, postalCode, city, country];
      return addressComponents.filter(Boolean).join(', ');
    }

    return '';
  }

  setFullAddress() {
    const fullAddress = this.createFullAddress();
    this.field.formControl.get('fullAddress')?.patchValue(fullAddress);
  }

  private updateGeolocationSignals(
    value: { latitude?: number | string | null; longitude?: number | string | null } | null
  ) {
    // `latitude`/`longitude` subfields default to '' (empty string). Number('')
    // is 0, which is a valid-but-wrong coordinate on the map, so short-circuit
    // on empty/null before converting.
    const lat = value?.latitude;
    const lng = value?.longitude;
    const latNum = lat === '' || lat == null ? NaN : Number(lat);
    const lngNum = lng === '' || lng == null ? NaN : Number(lng);

    if (Number.isFinite(latNum) && Number.isFinite(lngNum)) {
      this.hasGeolocation.set(true);
      this.locationData.set({ latitude: latNum, longitude: lngNum });
    } else {
      this.hasGeolocation.set(false);
      this.locationData.set(null);
    }
  }

  private completenessKey(value: Record<string, unknown> | null): string {
    // NOTE: latitude/longitude are deliberately excluded. When the user manually
    // clears coords, the key does not change and auto-geocode will not re-fire.
    // That is intentional — clearing coords is treated as a deliberate user action.
    const v = value ?? {};
    return [
      v['streetName'] ?? '',
      v['number'] ?? '',
      v['numberSuffix'] ?? '',
      v['postalCode'] ?? '',
      v['city'] ?? '',
      v['country'] ?? '',
    ].join('|');
  }

  private invalidateBagSnapshot(): void {
    const root = UtilityService.getFormOfRootForm(this.field);
    if (!root?.formControl?.get('meta-data')) return;

    // With events (not the previous `{ emitEvent: false }`) so Formly's own valueChanges
    // subscription syncs the meta-data page's model, AND so EntryPatchService can mark it
    // dirty — otherwise DetailsService.performSave (cross-page dirty sweep, #20789) never
    // sees this page and the cleared BAG snapshot is dropped on save. Safe against a
    // self-loop: the invalidation stream above subscribes to the ADDRESS control's
    // valueChanges, not meta-data's, so patching meta-data does not re-trigger it.
    this.entryPatchService.applyEntryPatchToRootForm(root, {
      'meta-data': {
        'bag-is-residential': null,
        'bag-matched-address': null,
        'bag-gebruiksdoel': null,
        'bag-oppervlakte': null,
        'bag-adresseerbaar-object-status': null,
        'bag-last-check-date': null,
      },
    });
    this.bagLastCheckDate.set(undefined);
    this.bagVerifyError.set(null);
    this.field.formControl.markAsDirty();
    this.field.options?.formState?.detailsService?.triggerAsyncValidation([
      { keyPath: 'entry.meta-data.bag-is-residential', value: null },
    ]);
  }

  private needsGeocode(): boolean {
    const v = this.field.formControl.value ?? {};
    // latitude/longitude fields default to '' (empty string), so treat any falsy value as "missing".
    return !!v.streetName && !!v.city && !!v.postalCode && (!v.latitude || !v.longitude);
  }

  private patchCoords(results: AddressSearchResult[]) {
    const match = results?.[0];
    if (!match || match.latitude == null || match.longitude == null) {
      debugLog('FormlyAddress:autoGeocode', 'no match for', this.createFullAddress());
      return;
    }
    this.field.formControl.patchValue({ latitude: match.latitude, longitude: match.longitude }, { emitEvent: false });
    this.updateGeolocationSignals(this.field.formControl.value);
  }

  private initializeAutoGeocode() {
    this.field.formControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        filter(() => !this.isDisabled()),
        debounceTime(600),
        map(v => this.completenessKey(v)),
        distinctUntilChanged(),
        filter(() => this.needsGeocode()),
        switchMap(() => {
          const query = this.createFullAddress();
          return this.addressesService
            .searchAddress({ body: { query } })
            .pipe(catchError(() => of([] as AddressSearchResult[])));
        }),
        tap(results => this.patchCoords(results))
      )
      .subscribe();
  }

  private initializeAddressSearch() {
    this.addressSearchControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        // Before the debounce on purpose: the single-line style must stop mirroring the stored
        // address the moment the first keystroke lands, not 400ms later.
        tap(() => (this.#isTypingQuery = true)),
        debounceTime(400),
        // Material writes the picked option's OBJECT into the control before it emits
        // optionSelected, hence the type guard: anything but a string is not a query.
        map(query => (typeof query === 'string' ? query.trim() : '')),
        // Deduplicate against the query that actually ran, not against the previous emission:
        // repeating a term the user has since cleared, left behind or picked must search again,
        // while a query that merely wanders away and settles back on what is already on screen
        // must not (#20984).
        filter(query => query !== this.#lastExecutedQuery),
        tap(query => (this.#lastExecutedQuery = query)),
        switchMap(trimmedQuery => {
          // Capture the token at request start. blur/clear/pick bump #queryToken without emitting
          // on this stream, so a response tagged with an out-of-date token has been abandoned.
          const token = ++this.#queryToken;
          if (!trimmedQuery) {
            this.addressSuggestions = [];
            this.isSearchingAddresses.set(false);
            this.addressSearchYieldedNothing.set(false);
            return of({ token, query: trimmedQuery, results: [] as AddressSearchResult[] });
          }

          this.isSearchingAddresses.set(true);
          return this.addressesService.searchAddress({ body: { query: trimmedQuery } }).pipe(
            catchError((err: HttpErrorResponse) => {
              // A failed query never produced results, so don't remember it either: retrying the
              // same term after the error message has to be allowed to reach the provider again.
              this.forgetExecutedQuery();
              this.notifyAddressSearchError(err);
              return of([] as AddressSearchResult[]);
            }),
            finalize(() => this.isSearchingAddresses.set(false)),
            map(results => ({ token, query: trimmedQuery, results }))
          );
        })
      )
      .subscribe(({ token, query, results }) => {
        if (token !== this.#queryToken) {
          // Blur / clear / pick (or a newer query) bumped the token after this request started.
          // Applying a stale response would resurrect the suggestion list or flag "Geen adres
          // gevonden" under a valid, already-restored address.
          return;
        }

        this.addressSuggestions = results;
        // Drives the "niets gevonden" hint next to the manual-entry button, so a user with an
        // address the provider doesn't know still has a way forward. Keyed on the query that was
        // searched, NOT on what the control happens to hold now: picking a suggestion makes
        // Material write the option OBJECT into the control, and that emission arrives here as an
        // empty query while the line already shows the chosen address — which the old check read
        // as "nothing found" and put the fallback hint under a perfectly good address.
        this.addressSearchYieldedNothing.set(results.length === 0 && query.length > 0);
      });
  }

  /**
   * Drops the memory of the last executed query so the very same term searches again. Called from
   * every route that abandons a query without emitting on the search stream.
   */
  private forgetExecutedQuery(): void {
    this.#lastExecutedQuery = null;
  }

  private notifyAddressSearchError(err: HttpErrorResponse): void {
    // Backend wraps provider failures as { error, message }. The message can be a large
    // provider JSON dump — log it for developers, show a short Dutch message to users.
    const backendMessage = err?.error?.message ?? err?.message;
    console.error('Address search failed', backendMessage ?? err);
    debugLog('FormlyAddress:search', 'address search failed', backendMessage ?? err);
    this.utilityService.notify('Adres zoeken mislukt. Probeer het later opnieuw.');
  }

  private applySelectedAddress(address: AddressSearchResult) {
    this.field.formControl.markAsDirty();
    this.field.formControl.markAsTouched();
    // Before patchValue, not after: patchValue emits valueChanges synchronously, and the handler
    // there refuses to touch the visible line while a query is being typed. Clearing the flag
    // first is what lets the picked address replace the query text right away instead of only on
    // the next blur.
    this.#isTypingQuery = false;
    // Abandon any in-flight request: the user committed to a suggestion, so a late response for the
    // same typed query must not re-open the list on top of the chosen address.
    this.#queryToken++;
    this.forgetExecutedQuery();
    this.field.formControl.patchValue({
      ...this.field.formControl.value,
      ...address,
      manual: false,
    });
    // Picking a suggestion in the single-line style fills the one line; the individual fields stay
    // behind the manual-entry link, which is the whole point of that layout.
    if (!this.singleLine()) {
      this.showAddressFields.set(true);
    }

    // Trigger async validation after address autofill
    const detailsService = this.field.options?.formState?.detailsService;
    if (detailsService && this.field.props?.enableAsyncValidation) {
      detailsService.triggerAsyncValidation();
    }
  }

  /**
   * Renders whatever Material puts in the control back to a string. The control normally holds the
   * typed query (a string); only during option selection does it briefly hold the option object.
   * Declared as an arrow property so the template can hand the reference to [displayWith] without
   * losing `this`.
   */
  readonly displaySuggestion = (value: AddressSearchResult | string | null): string => {
    if (typeof value === 'string') {
      return value;
    }
    return value ? this.formatSuggestionLabel(value) : '';
  };

  formatSuggestionLabel(address: AddressSearchResult): string {
    const number = address.number != null ? `${address.number}${address.numberSuffix ?? ''}` : '';
    const street = [address.streetName, number].filter(Boolean).join(' ');
    return [street, address.postalCode, address.city, address.country].filter(Boolean).join(', ');
  }

  copyAddress(): void {
    // The stored fullAddress can lag a manual edit by one change cycle, so fall back to the
    // freshly composed parts rather than copying a stale line.
    const address = this.field.formControl.get('fullAddress')?.value || this.createFullAddress();
    if (address) {
      this.clipboard.copy(address);
      this.addressCopied.set(true);
      setTimeout(() => this.addressCopied.set(false), 2000);
    }
  }

  verifyBag(): void {
    if (this.isDisabled()) {
      return;
    }

    const mappingId = this.props['bagApiMappingId'] as string | undefined;
    const entity = this.options?.formState?.entity;
    const entry = this.options?.formState?.entry;
    if (!mappingId || !entity || !entry) {
      debugLog('FormlyAddress:bagVerify', 'skipped — missing mappingId/entity/entry', {
        hasMappingId: !!mappingId,
        hasEntity: !!entity,
        hasEntry: !!entry,
      });
      return;
    }

    this.isVerifyingBag.set(true);
    this.bagVerifyError.set(null);
    const verificationAddressKey = this.completenessKey(this.field.formControl.value);

    this.apiMappingsStore
      .applyMapping({
        mappingId,
        moduleId: this.options.formState.apiMappingModuleId ?? entity.id,
        entry,
        message: false,
      })
      .pipe(
        takeUntil(this.destroy$),
        catchError(err => {
          console.error('BAG verify failed', err);
          this.bagVerifyError.set('BAG verificatie mislukt. Probeer het opnieuw.');
          this.isVerifyingBag.set(false);
          return of(null);
        })
      )
      .subscribe(result => {
        if (!result) {
          this.isVerifyingBag.set(false);
          return;
        }

        if (this.completenessKey(this.field.formControl.value) !== verificationAddressKey) {
          this.isVerifyingBag.set(false);
          return;
        }

        const isResidential = result.entry?.['meta-data']?.['bag-is-residential'];
        if (typeof isResidential !== 'boolean') {
          this.invalidateBagSnapshot();
          this.bagVerifyError.set('BAG verificatie gaf geen geldige uitkomst. Probeer het opnieuw.');
          this.isVerifyingBag.set(false);
          return;
        }

        const rootField = UtilityService.getFormOfRootForm(this.field);
        const checkedAt = new Date().toISOString();
        this.#applyingBagResult = true;
        try {
          // Uses EntryPatchService (not a plain patchValue) because the BAG result lands on
          // the `meta-data` page, which can differ from the currently active page — without
          // marking it dirty, DetailsService.performSave's page-only save silently drops it
          // (#20789). #applyingBagResult keeps suppressing the invalidation stream while both
          // patches below apply.
          this.entryPatchService.applyEntryPatchToRootForm(rootField, result.entry);
          // Stamp verification timestamp — backend mapping cannot produce currentDate.
          this.entryPatchService.applyEntryPatchToRootForm(rootField, {
            'meta-data': { 'bag-last-check-date': checkedAt },
          });
        } finally {
          this.#applyingBagResult = false;
        }
        this.bagLastCheckDate.set(checkedAt);
        this.field.options?.formState?.detailsService?.triggerAsyncValidation([
          { keyPath: 'entry.meta-data.bag-is-residential', value: isResidential },
        ]);
        this.isVerifyingBag.set(false);
      });
  }

  private hasExistingAddress(): boolean {
    const value = this.field.formControl.value;
    if (!value) {
      return false;
    }

    const fieldsToCheck = ['streetName', 'number', 'postalCode', 'city', 'country'];
    return fieldsToCheck.some(key => Boolean(value?.[key]));
  }
}
